import {FUNNELS_MATH_TYPES, ProjectFeatureFlag, ResourceType, RETENTION_MATH_TYPES} from 'common/report/constants';
import {FormulaEntryRow} from 'common/query-builder/query-entry-section/row-types/formula-entry';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {FunnelOrder} from 'common/report/funnels/models/constants';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {RetentionAlignmentType} from 'common/report/retention/util/types';
import {MetricType} from 'common/query-builder/query-block/constants';

import {
  isFormulaEntry,
  isMetricEntry,
  MetricEntry,
  QuerySuperEntry,
  SelectorType,
  SelectorTypeDefinitions,
} from '../types';
import {EntryRowConstructor, EntryRowProps} from './types';
import {FilterEntryRow} from './filter';
import {FlowsEntryRow} from './flows-entry';
import {GroupedEntryRow} from './grouped-entry';
import {CohortGroupEntryRow} from './inline-cohort';
import {SegmentEntryRow} from './segment-entry';
import {SuperEntryRow} from './super-entry';
import {FunnelsMetricEntryRow} from './metric-entry/metrics/funnels';
import {RetentionMetricEntryRow} from './metric-entry/metrics/retention';

type EntryRowConstructorMap = {[key in SelectorType]?: EntryRowConstructor<QuerySuperEntry | MetricEntry>};

/**
 * Returns an instance of EntryRow that handles selection logic for a given entry type
 */
export function createEntryRow(
  type: SelectorType,
  props: EntryRowProps<QuerySuperEntry | MetricEntry>,
  _featureFlags: {[k in ProjectFeatureFlag]?: boolean} = {},
) {
  if (type === `metric-entry` && isMetricEntry(props.entry)) {
    const MetricTypeRowMap: EntryRowConstructorMap = {
      [MetricType.People]: null,
      [MetricType.Cohort]: null,
      [MetricType.Event]: null,
      [MetricType.CustomEvent]: null,
      [MetricType.Funnel]: FunnelsMetricEntryRow,
      [MetricType.Retention]: RetentionMetricEntryRow,
      [MetricType.Formula]: null,
    };

    const rowClass = MetricTypeRowMap[props.entry.behavior.type];
    return new rowClass(props);
  }
  /** Defines a mapping for row constructors for all entry types */
  const SelectorTypeRowMap: EntryRowConstructorMap = {
    [ResourceType.Formula]: FormulaEntryRow,
    [SelectorTypeDefinitions.GroupedEvent]: GroupedEntryRow,
    [SelectorTypeDefinitions.Filter]: FilterEntryRow,
    [SelectorTypeDefinitions.FlowsEvent]: FlowsEntryRow,
    [SelectorTypeDefinitions.CohortGroup]: CohortGroupEntryRow,
    [SelectorTypeDefinitions.Segment]: SegmentEntryRow,
  };

  const rowClass = SelectorTypeRowMap[type] ?? SuperEntryRow;
  return new rowClass(props);
}

/**
 * Generates an empty entry for a given selector type
 */
export function getNewEmptyEntry({
  type,
  section,
}: {
  type: SelectorType;
  section: QueryEntrySection;
}): QuerySuperEntry | MetricEntry {
  const emptyRowMap = {
    [SelectorTypeDefinitions.Filter]: () => FilterEntryRow.getNewEntry(),
    [SelectorTypeDefinitions.FlowsEvent]: () => FlowsEntryRow.getNewEntry(),
    [ResourceType.Formula]: () => {
      return FormulaEntryRow.getNewEntry({
        existingFormulaNames: section
          .getEntries()
          .filter(isFormulaEntry)
          .map((entry) => entry.event.label),
      });
    },
    [SelectorTypeDefinitions.PropertyExpansion]: () => ({
      type: SelectorTypeDefinitions.PropertyExpansion,
      event: null,
      filters: [],
    }),
    [MetricType.Funnel]: () =>
      ({
        type: `metric-entry`,
        behavior: {
          type: MetricType.Funnel,
          name: null,
          conversionWindowDuration: 30,
          conversionWindowUnit: `day`,
          funnelOrder: FunnelOrder.Loose,
          aggregateBy: [],
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
          ],
        },
        measurement: {
          aggregationOperator: FUNNELS_MATH_TYPES.CONVERSION_RATE,
          stepIndex: null,
        },
        isExpanded: true,
      } as MetricEntry),
    [MetricType.Retention]: () =>
      ({
        type: `metric-entry`,
        behavior: {
          type: MetricType.Retention,
          name: null,
          retentionType: `birth`,
          retentionAlignmentType: RetentionAlignmentType.Birth,
          retentionUnit: TimeUnit.Day, // TODO
          retentionUnbounded: false,
          retentionCustomBucketSizes: [],
          behaviors: [
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
            {
              type: MetricType.Event,
              name: null,
              filters: [],
            },
          ],
        },
        measurement: {
          aggregationOperator: RETENTION_MATH_TYPES.RETENTION_RATE,
          retentionBuckets: [],
          retentionBucketIndex: 0,
        },
        isExpanded: true,
      } as MetricEntry),
  };

  // TODO(pablo): return null as a way of expressing this should be delegated
  // to mp-query-entry-section, this will be removed to always return from
  // generating an empty entry from the given EntryRow class that's associated
  // with that type of entry
  return emptyRowMap[type]?.();
}
